/**
 * /mrp/clamp-forecast — perpetual forecast grid for clamp/fiber categories.
 *
 * One row per `mrp.clamp_category`, one column per calendar month in a sliding
 * window. Each cell shows the editable forecast quantity above the running
 * total shipped from any linked POs (so the planner can compare forecast vs
 * actual without leaving the page). Selecting a cell opens a side panel with
 * revision history, linked POs, and a "Create PO" action (single line for
 * non-kit categories, BoM-style multi-line for kit categories).
 */
import {
  Fragment,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import {
  ExternalLink,
} from "lucide-react";
import {
  Link,
} from "react-router-dom";
import {
  useMutation,
  useQuery,
  useQueryClient,
  type QueryClient,
} from "@tanstack/react-query";
import {
  apiFetch,
  ApiError,
} from "@/lib/api";
import {
  cn,
} from "@/lib/utils";
import {
  Button,
} from "@/components/ui/Button";
import {
  MrpSkuDetailLink,
} from "@/components/MrpSkuDetailLink";
import {
  DateInput,
  Input,
  Label,
} from "@/components/ui/Input";
import {
  HoverHintContent,
  HoverHintWithGlyph,
} from "@/components/ui/title-help";
import {
  defaultClampForecastExpectedArrivalDate,
} from "@integrator-src/mrp/clamp-forecast-expected-arrival";
import {
  resolveClampForecastExpectedArrivalDate,
} from "@integrator-src/mrp/clamp-forecast-resolve-expected-arrival";
import {
  megaventoryPurchaseOrderDocumentUrl,
} from "@/lib/megaventoryDocumentUrls";
import {
  buildClampForecastPurchaseOrderComments,
} from "@integrator-src/mrp/clamp-forecast-po-comments";
import {
  CLAMP_FORECAST_WYJ_DEFAULT_CURRENCY,
  CLAMP_FORECAST_WYJ_DEFAULT_PAYMENT_TERMS,
  CLAMP_FORECAST_WYJ_SUPPLIER_DATA_ID,
  CLAMP_FORECAST_WYJ_SUPPLIER_LABEL,
  formatClampForecastAddToPoOptionLabel,
} from "@integrator-src/mrp/clamp-forecast-add-to-po-options";
import {
  computeClampForecastOrderedForDisplay,
  computeRemainingClampForecastAllocation,
} from "@integrator-src/mrp/clamp-forecast-po-lines";
import {
  type ClampForecastMonthPoPlan,
} from "@integrator-src/mrp/clamp-forecast-po-lines";
import {
  buildMrpIncomingPartsPath,
  buildMrpSkuDetailPath,
  firstOfThisMonth,
  formatMonth,
  formatNumber,
  MRP_LOCATIONS,
  type MrpLocation,
  type MrpSkuHoverHintFields,
} from "@/lib/mrp";
import {
  recomputeCategoryClampForecastRollForward,
} from "@/lib/clampForecastRollForward";
import {
  isClampForecastRollForwardRefreshing,
} from "@/lib/clampForecastRollForwardState";

export const PAGE_HOVER_HINT =
  "Perpetual forecast for WYJ clamp/fiber ships, modeled on the rep ↔ planner Schedule workbook. " +
  "One row per category (one finished-good SKU per non-kit row; two kit rows that BoM-explode at PO time). " +
  "Edit a cell to update the forecast; each change is appended to mrp.clamp_forecast_revision with the actor and an optional note. " +
  "Click a cell to open the side panel for revision history, linked POs, ghost shipment adjustments, and the Create PO action. " +
  "Ghost numbers simulate higher or lower WYJ shipments for a month without changing the saved forecast. " +
  "Forecast and POs are independently editable: changing a PO does not move the forecast.";

export const INVENTORY_HOVER_HINT =
  "Latest mrp.inventory_snapshot physical on-hand per (SKU, location). Coated-clamp rows sum raw, QC, and TLA physical stock by site. The middle grid line rolls MRP planning quantity forward month by month.";

/** Months before the selected start month in the grid (with explicit start_month, extends horizon forward only). */
export const PAST_MONTHS = 0;
export const START_MONTH_OPTIONS_BEFORE = 24;
export const START_MONTH_OPTIONS_AFTER = 36;

export function addMonthsToMonthIso(monthIso: string, delta: number): string {
  const year = Number.parseInt(monthIso.slice(0, 4), 10);
  const month = Number.parseInt(monthIso.slice(5, 7), 10);
  const date = new Date(Date.UTC(year, month - 1 + delta, 1));
  return `${date.getUTCFullYear()}-${String(date.getUTCMonth() + 1).padStart(2, "0")}-01`;
}

export function buildStartMonthOptions(anchorMonth: string, monthsBefore: number, monthsAfter: number): string[] {
  const options: string[] = [];
  for (let offset = -monthsBefore; offset <= monthsAfter; offset += 1) {
    options.push(addMonthsToMonthIso(anchorMonth, offset));
  }
  return options;
}

export const CALLOUT_WIDTH = 42 * 16;

export const START_MONTH_SELECT_CLASS =
  "h-10 rounded-md border border-slate-300 bg-white px-3 text-sm text-slate-900 focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500";

export const CLAMP_FORECAST_START_MONTH_SESSION_KEY = "mrp.clamp-forecast.start-month";

export function isClampForecastMonthIso(value: string): boolean {
  return /^\d{4}-\d{2}-01$/.test(value.trim());
}

export function readClampForecastStartMonthFromSession(fallback: string, allowed: string[]): string {
  try {
    const raw = sessionStorage.getItem(CLAMP_FORECAST_START_MONTH_SESSION_KEY);
    if (raw != null && isClampForecastMonthIso(raw) && allowed.includes(raw.trim())) {
      return raw.trim();
    }
  } catch {
    // sessionStorage unavailable (private mode, blocked storage, etc.)
  }
  return fallback;
}

export function writeClampForecastStartMonthToSession(monthIso: string): void {
  try {
    sessionStorage.setItem(CLAMP_FORECAST_START_MONTH_SESSION_KEY, monthIso);
  } catch {
    // sessionStorage unavailable
  }
}

export function initialClampForecastStartMonth(): string {
  const anchor = firstOfThisMonth();
  const options = buildStartMonthOptions(anchor, START_MONTH_OPTIONS_BEFORE, START_MONTH_OPTIONS_AFTER);
  return readClampForecastStartMonthFromSession(anchor, options);
}

/** While the grid refetches derived demand / PO lines, soften stale numbers in place. */
export const GRID_REFRESHING_NUMBER_CLASS =
  "blur-[2px] opacity-45 saturate-50 transition-[filter,opacity] duration-200";

export interface ClampForecastCategoryRow {
  code: string;
  display_name: string;
  sort_order: number;
  is_kit: boolean;
  fg_sku: string | null;
  notes: string | null;
  active: boolean;
  updated_at: string;
  updated_by: string | null;
}

export interface ClampForecastKitItemRow {
  id: number;
  category_code: string;
  component_sku: string;
  quantity_per_kit: number;
  sort_order: number;
  notes: string | null;
}

export interface ClampForecastCellRow {
  id: number;
  category_code: string;
  plan_month: string;
  forecast_qty: number;
  expected_arrival_date: string | null;
  notes: string | null;
  updated_at: string;
  updated_by: string | null;
}

export interface ClampForecastGhostRow {
  id: number;
  category_code: string;
  plan_month: string;
  ghost_qty: number;
  expected_arrival_date: string | null;
  notes: string | null;
  updated_at: string;
  updated_by: string | null;
}

export interface ClampForecastAddToPoOption {
  purchase_order_id: number;
  mv_number: string | null;
  supplier_name: string | null;
  line_count: number;
  special_instructions: string | null;
}

export interface ClampForecastPoLinkRow {
  id: number;
  forecast_id: number;
  purchase_order_item_id: number;
  purchase_order_id: number;
  purchase_order_mv_id: number | null;
  mv_number: string | null;
  sku: string;
  quantity_ordered: number;
  unit_price: number | null;
  quantity_received: number;
  expected_arrival: string | null;
  expected_qc_complete_date: string | null;
  supplier_name: string | null;
  special_instructions: string | null;
  created_at: string;
}

export interface ClampForecastInventoryRow {
  sku: string;
  location_code: string;
  quantity: number;
  stock_physical: number;
  incoming_work_orders: number;
  incoming_purchase_orders: number;
  outgoing_work_orders: number;
}

export interface ClampForecastDerivedDemandSkuRow {
  category_code: string;
  sku: string;
  sort_order: number;
  is_default: boolean;
  notes: string | null;
}

export interface ClampForecastDerivedDemandRow {
  category_code: string;
  derived_demand_sku: string;
  plan_month: string;
  location_code: string;
  rolled_qty_pre_order: number;
  starting_qty: number;
  arrivals: number;
  demand: number;
  next_component_order_date: string | null;
  pipeline_breakdown?: {
    sku: string;
    rolled_qty_pre_order: number;
    starting_qty: number;
    arrivals: number;
    demand: number;
  }[];
}

export interface ClampForecastGridResponse {
  start_month: string;
  end_month_exclusive: string;
  past_months: number;
  forward_months: number;
  months: string[];
  categories: ClampForecastCategoryRow[];
  kit_items: ClampForecastKitItemRow[];
  cells: ClampForecastCellRow[];
  ghosts: ClampForecastGhostRow[];
  po_links: ClampForecastPoLinkRow[];
  inventory: ClampForecastInventoryRow[];
  derived_demand_skus: ClampForecastDerivedDemandSkuRow[];
  derived_demand: ClampForecastDerivedDemandRow[];
}

export type ClampForecastShellResponse = Omit<ClampForecastGridResponse, "derived_demand">;

export interface ClampForecastDerivedDemandResponse {
  derived_demand_base: ClampForecastDerivedDemandRow[];
  derived_demand: ClampForecastDerivedDemandRow[];
}

export interface ClampForecastRevisionRow {
  id: number;
  forecast_id: number;
  old_quantity: number | null;
  new_quantity: number;
  changed_by: string | null;
  note: string | null;
  changed_at: string;
}

export interface CreatePoResponse {
  mv_purchase_order_id: number | null;
  public_purchase_order_id: number;
  po_number?: string | null;
  forecast_po_links_created: number;
}

export interface CreateMonthPoResponse extends CreatePoResponse {
  included_cell_count: number;
  po_line_count: number;
  skipped_cells: { forecast_id: number; category_code: string; reason: string }[];
}

export function cellKey(categoryCode: string, monthIso: string): string {
  return `${categoryCode}::${monthIso}`;
}

export type ClampForecastShellQueryKey = readonly [
  "mrp",
  "clamp-forecast",
  "shell",
  { start_month: string; past_months: number },
];

export type ClampForecastDerivedDemandQueryKey = readonly [
  "mrp",
  "clamp-forecast",
  "derived-demand",
  { start_month: string; past_months: number },
];

export function clampForecastShellQueryKey(startMonth: string, pastMonths: number): ClampForecastShellQueryKey {
  return ["mrp", "clamp-forecast", "shell", { start_month: startMonth, past_months: pastMonths }];
}

export function clampForecastDerivedDemandQueryKey(
  startMonth: string,
  pastMonths: number,
): ClampForecastDerivedDemandQueryKey {
  return ["mrp", "clamp-forecast", "derived-demand", { start_month: startMonth, past_months: pastMonths }];
}

export function patchClampForecastGridCell(
  queryClient: QueryClient,
  queryKey: ClampForecastShellQueryKey,
  cell: ClampForecastCellRow,
): void {
  queryClient.setQueryData<ClampForecastShellResponse>(queryKey, (old) => {
    if (old == null) return old;
    const cells = [...old.cells];
    const index = cells.findIndex(
      (row) => row.category_code === cell.category_code && row.plan_month === cell.plan_month,
    );
    if (index >= 0) {
      cells[index] = cell;
    } else {
      cells.push(cell);
    }
    return { ...old, cells };
  });
}

export function removeClampForecastGridCell(
  queryClient: QueryClient,
  queryKey: ClampForecastShellQueryKey,
  categoryCode: string,
  planMonth: string,
): void {
  queryClient.setQueryData<ClampForecastShellResponse>(queryKey, (old) => {
    if (old == null) return old;
    return {
      ...old,
      cells: old.cells.filter(
        (row) => !(row.category_code === categoryCode && row.plan_month === planMonth),
      ),
    };
  });
}

export function patchCategoryDerivedDemandRows(
  queryClient: QueryClient,
  derivedQueryKey: ClampForecastDerivedDemandQueryKey,
  categoryCode: string,
  derivedDemand: ClampForecastDerivedDemandRow[],
): void {
  queryClient.setQueryData<ClampForecastDerivedDemandResponse>(derivedQueryKey, (old) => ({
    derived_demand_base: old?.derived_demand_base ?? [],
    derived_demand: [
      ...(old?.derived_demand ?? []).filter((row) => row.category_code !== categoryCode),
      ...derivedDemand,
    ],
  }));
}

export function rechainCategoryRollForwardInCache(
  queryClient: QueryClient,
  shellQueryKey: ClampForecastShellQueryKey,
  derivedQueryKey: ClampForecastDerivedDemandQueryKey,
  categoryCode: string,
): boolean {
  const shell = queryClient.getQueryData<ClampForecastShellResponse>(shellQueryKey);
  const derived = queryClient.getQueryData<ClampForecastDerivedDemandResponse>(derivedQueryKey);
  if (shell == null || derived?.derived_demand_base == null) return false;

  const category = shell.categories.find((row) => row.code === categoryCode);
  if (category == null || category.is_kit) return false;

  const skuRows = shell.derived_demand_skus.filter((row) => row.category_code === categoryCode);
  const pipeline = buildClampSupplyPipeline(category, skuRows);
  const rawPurchasedSku = pipeline?.raw ?? category.fg_sku;

  const recomputed = recomputeCategoryClampForecastRollForward(
    categoryCode,
    category,
    derived.derived_demand_base,
    shell.cells,
    shell.po_links.map((link) => ({
      forecast_id: link.forecast_id,
      expected_arrival: link.expected_arrival,
      sku: link.sku,
      quantity_ordered: link.quantity_ordered,
    })),
    rawPurchasedSku,
    shell.kit_items,
    shell.ghosts.map((ghost) => ({
      category_code: ghost.category_code,
      plan_month: ghost.plan_month,
      ghost_qty: ghost.ghost_qty,
      expected_arrival_date: ghost.expected_arrival_date,
    })),
  );
  patchCategoryDerivedDemandRows(queryClient, derivedQueryKey, categoryCode, recomputed);
  return true;
}

export function patchCategoryPoLinksInShell(
  queryClient: QueryClient,
  shellQueryKey: ClampForecastShellQueryKey,
  categoryCode: string,
  poLinks: ClampForecastPoLinkRow[],
): void {
  queryClient.setQueryData<ClampForecastShellResponse>(shellQueryKey, (old) => {
    if (old == null) return old;
    return {
      ...old,
      po_links: [
        ...old.po_links.filter((link) => {
          const linkedCell = old.cells.find((cell) => cell.id === link.forecast_id);
          if (linkedCell == null) return false;
          return linkedCell.category_code !== categoryCode;
        }),
        ...poLinks,
      ],
    };
  });
}

export function indexCells(cells: ClampForecastCellRow[]): Map<string, ClampForecastCellRow> {
  const map = new Map<string, ClampForecastCellRow>();
  for (const cell of cells) {
    map.set(cellKey(cell.category_code, cell.plan_month), cell);
  }
  return map;
}

export function indexGhosts(ghosts: ClampForecastGhostRow[]): Map<string, ClampForecastGhostRow> {
  const map = new Map<string, ClampForecastGhostRow>();
  for (const ghost of ghosts) {
    map.set(cellKey(ghost.category_code, ghost.plan_month), ghost);
  }
  return map;
}

export function patchClampForecastGhostInShell(
  queryClient: QueryClient,
  queryKey: ClampForecastShellQueryKey,
  ghost: ClampForecastGhostRow | null,
  categoryCode: string,
  planMonth: string,
): void {
  queryClient.setQueryData<ClampForecastShellResponse>(queryKey, (old) => {
    if (old == null) return old;
    const ghosts = old.ghosts.filter(
      (row) => !(row.category_code === categoryCode && row.plan_month === planMonth),
    );
    if (ghost != null) {
      ghosts.push(ghost);
    }
    return { ...old, ghosts };
  });
}

export function indexPoLinks(links: ClampForecastPoLinkRow[]): Map<number, ClampForecastPoLinkRow[]> {
  const map = new Map<number, ClampForecastPoLinkRow[]>();
  for (const link of links) {
    const list = map.get(link.forecast_id) ?? [];
    list.push(link);
    map.set(link.forecast_id, list);
  }
  return map;
}

export function normalizeSkuKey(sku: string): string {
  return sku.trim().toUpperCase();
}

export function indexInventoryBySku(rows: ClampForecastInventoryRow[]): Map<string, ClampForecastInventoryRow[]> {
  const map = new Map<string, ClampForecastInventoryRow[]>();
  for (const row of rows) {
    const key = normalizeSkuKey(row.sku);
    const list = map.get(key) ?? [];
    list.push(row);
    map.set(key, list);
  }
  return map;
}

export function lookupInventoryRows(
  inventoryIndex: Map<string, ClampForecastInventoryRow[]>,
  sku: string,
): ClampForecastInventoryRow[] {
  return inventoryIndex.get(normalizeSkuKey(sku)) ?? [];
}

/** Physical snapshot count for on-hand chips (not planning qty net of WO/SO reservations). */
export function inventoryPhysicalOnHand(row: ClampForecastInventoryRow): number {
  return row.stock_physical;
}

/**
 * Group derived-demand rows by `${category_code}::${plan_month}` so each grid
 * cell can grab its location breakdown in O(1). Each value is the full per-
 * location list across whichever derived-demand SKUs are mapped to the
 * category (typically one SKU like 90-002-TLA for clamps).
 */
export function indexDerivedDemandByCell(rows: ClampForecastDerivedDemandRow[]): Map<string, ClampForecastDerivedDemandRow[]> {
  const map = new Map<string, ClampForecastDerivedDemandRow[]>();
  for (const row of rows) {
    const key = cellKey(row.category_code, row.plan_month);
    const list = map.get(key) ?? [];
    list.push(row);
    map.set(key, list);
  }
  return map;
}

export function indexDerivedDemandSkusByCategory(
  rows: ClampForecastDerivedDemandSkuRow[],
): Map<string, ClampForecastDerivedDemandSkuRow[]> {
  const map = new Map<string, ClampForecastDerivedDemandSkuRow[]>();
  for (const row of rows) {
    const list = map.get(row.category_code) ?? [];
    list.push(row);
    map.set(row.category_code, list);
  }
  return map;
}

/** Sum rolled-forward pre-order qty across all locations for a single cell. */
export function sumRolledQtyAcrossLocations(rows: ClampForecastDerivedDemandRow[] | undefined): number | null {
  if (rows == null || rows.length === 0) return null;
  let total = 0;
  for (const row of rows) {
    total += row.rolled_qty_pre_order;
  }
  return total;
}

/**
 * `formatNumber` adds the locale minus sign for negatives, but we keep the
 * dedicated wrapper so the grid signals shortfalls with a colour without
 * sprinkling conditionals at every callsite.
 */
/** Shared column widths so roll math lines up across every location block. */
export function PipelineRollFormulaCells({
  starting,
  arrivals,
  demand,
  rolled,
}: {
  starting: number;
  arrivals: number;
  demand: number;
  rolled: number;
}): JSX.Element {
  const negative = rolled < 0;
  return (
    <>
      <td className="w-[2.85rem] py-0 pl-1 text-right tabular-nums text-slate-600">{formatNumber(starting)}</td>
      <td className="w-2 py-0 text-center text-slate-400">+</td>
      <td className="w-[2.85rem] py-0 text-right tabular-nums text-slate-600">{formatNumber(arrivals)}</td>
      <td className="w-2 py-0 text-center text-slate-400">−</td>
      <td className="w-[2.85rem] py-0 text-right tabular-nums text-slate-600">{formatNumber(demand)}</td>
      <td className="w-2 py-0 text-center text-slate-400">=</td>
      <td
        className={`w-[2.85rem] py-0 text-right tabular-nums ${negative ? "font-medium text-rose-700" : "text-slate-700"}`}
      >
        {formatNumber(rolled)}
      </td>
    </>
  );
}

export function PipelineRollBreakdownTable({
  rows,
  supplyPipeline,
}: {
  rows: ClampForecastDerivedDemandRow[];
  supplyPipeline: ClampSupplyPipeline | null;
}): JSX.Element {
  return (
    <table className="w-max max-w-full border-collapse font-mono text-[11px] leading-tight">
      <tbody>
        {rows.map((row, index) => {
          const starting = row.starting_qty ?? 0;
          const arrivals = row.arrivals ?? 0;
          const demand = row.demand ?? 0;
          const rolled = row.rolled_qty_pre_order;
          const breakdown = row.pipeline_breakdown;
          return (
            <Fragment key={row.location_code}>
              <tr className={index > 0 ? "border-t border-slate-200" : undefined}>
                <td colSpan={8} className="pb-0.5 pt-1.5">
                  <span className="flex justify-between gap-4 uppercase tracking-wide text-slate-600">
                    <span>{row.location_code}</span>
                    <span className={rolled < 0 ? "font-medium text-rose-700" : "text-slate-800"}>
                      {formatNumber(rolled)}
                    </span>
                  </span>
                </td>
              </tr>
              {breakdown != null && breakdown.length > 0
                ? breakdown.map((part) => (
                    <tr key={`${row.location_code}::${part.sku}`} className="text-slate-600">
                      <td className="whitespace-nowrap py-0 pr-1 text-slate-700">
                        {pipelineStageLabel(part.sku, supplyPipeline)} {part.sku}
                      </td>
                      <PipelineRollFormulaCells
                        starting={part.starting_qty ?? 0}
                        arrivals={part.arrivals ?? 0}
                        demand={part.demand ?? 0}
                        rolled={part.rolled_qty_pre_order}
                      />
                    </tr>
                  ))
                : (
                    <tr className="text-slate-600">
                      <td className="whitespace-nowrap py-0 pr-1 text-slate-700">Raw</td>
                      <PipelineRollFormulaCells
                        starting={starting}
                        arrivals={arrivals}
                        demand={demand}
                        rolled={rolled}
                      />
                    </tr>
                  )}
            </Fragment>
          );
        })}
      </tbody>
    </table>
  );
}

export function pipelineStageLabel(sku: string, pipeline: ClampSupplyPipeline | null): string {
  if (pipeline == null) return sku;
  if (normalizeSkuKey(sku) === normalizeSkuKey(pipeline.raw)) return "Raw";
  if (pipeline.qc != null && normalizeSkuKey(sku) === normalizeSkuKey(pipeline.qc)) return "QC";
  if (normalizeSkuKey(sku) === normalizeSkuKey(pipeline.tla)) return "TLA";
  return sku;
}

export function formatSignedRolledQty(value: number | null): string {
  if (value == null) return "—";
  return formatNumber(value);
}

export function formatClampForecastUnitPrice(value: number | null): string {
  if (value == null || !Number.isFinite(value)) {
    return "no unit cost";
  }
  return `${value.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 4,
  })} unit cost`;
}

export interface ClampSupplyPipeline {
  raw: string;
  qc: string | null;
  tla: string;
}

/** Coated clamp purchased SKU (90-00X-04) → QC operation SKU (90-00X-QC). */
export function inferClampQcSkuFromPurchasedFg(fgSku: string): string | null {
  const trimmed = fgSku.trim();
  const match = /^90-00(\d)-04$/.exec(trimmed);
  if (!match) return null;
  return `90-00${match[1]}-QC`;
}

/**
 * When the MRP roll uses an upstream assembly SKU (TLA) that differs from the
 * purchased finished-good (raw coated clamp), return the three pipeline stages
 * so the row label can show why on-hand numbers diverge.
 */
export function buildClampSupplyPipeline(
  category: ClampForecastCategoryRow,
  derivedSkuRows: ClampForecastDerivedDemandSkuRow[],
): ClampSupplyPipeline | null {
  if (category.is_kit || category.fg_sku == null) return null;
  const raw = category.fg_sku.trim();
  if (raw === "") return null;
  const upstreamSkus = derivedSkuRows.map((row) => row.sku.trim()).filter((sku) => sku.length > 0);
  const tla = upstreamSkus.find((sku) => sku !== raw) ?? null;
  if (tla == null) return null;
  return { raw, qc: inferClampQcSkuFromPurchasedFg(raw), tla };
}

export function orderedForForecastCell(
  category: ClampForecastCategoryRow,
  kitItems: ClampForecastKitItemRow[],
  forecastQty: number,
  poLinks: ClampForecastPoLinkRow[] | undefined,
): number {
  if (poLinks == null || poLinks.length === 0) return 0;
  return computeClampForecastOrderedForDisplay({
    forecast_qty: forecastQty,
    category: { is_kit: category.is_kit, fg_sku: category.fg_sku },
    kitItems,
    linkedLines: poLinks.map((link) => ({
      sku: link.sku,
      quantity_ordered: link.quantity_ordered,
    })),
  });
}

export function clampForecastPoLinkShowsSpecialInstructions(
  link: ClampForecastPoLinkRow,
  poLinks: ClampForecastPoLinkRow[],
): boolean {
  const instructions = link.special_instructions?.trim() ?? "";
  if (instructions === "") return false;
  const firstForPo = poLinks.find(
    (candidate) =>
      candidate.purchase_order_id === link.purchase_order_id &&
      (candidate.special_instructions?.trim() ?? "") !== "",
  );
  return firstForPo?.id === link.id;
}

export function ClampForecastPoDocumentLink({
  mvNumber,
  purchaseOrderId,
  purchaseOrderMvId,
}: {
  mvNumber: string | null;
  purchaseOrderId: number;
  purchaseOrderMvId: number | null;
}): JSX.Element {
  const label = mvNumber ?? `PO #${purchaseOrderId}`;
  return (
    <span className="inline-flex min-w-0 items-center gap-1">
      <Link
        to={`/mrp/purchase-orders/${purchaseOrderId}`}
        className="font-mono text-brand-700 hover:underline"
      >
        {label}
      </Link>
      {purchaseOrderMvId != null ? (
        <a
          href={megaventoryPurchaseOrderDocumentUrl(purchaseOrderMvId, mvNumber)}
          target="_blank"
          rel="noreferrer noopener"
          className="inline-flex shrink-0 text-slate-400 hover:text-brand-600"
          aria-label={`Open purchase order ${label} in Megaventory`}
        >
          <ExternalLink className="h-3.5 w-3.5" aria-hidden />
        </a>
      ) : null}
    </span>
  );
}

export function isToday(monthIso: string): boolean {
  const now = new Date();
  const thisMonth = `${now.getUTCFullYear()}-${String(now.getUTCMonth() + 1).padStart(2, "0")}-01`;
  return thisMonth === monthIso;
}

export function describeApiError(error: unknown): string {
  if (error instanceof ApiError) return error.message;
  if (error instanceof Error) return error.message;
  return String(error);
}


export function FieldLabelWithHelp({
  htmlFor,
  label,
  hoverHint,
}: {
  htmlFor?: string;
  label: string;
  hoverHint: string;
}): JSX.Element {
  return (
    <div className="flex flex-wrap items-center gap-1.5">
      <Label htmlFor={htmlFor}>{label}</Label>
      <HoverHintWithGlyph size="sm" hoverHint={hoverHint} />
    </div>
  );
}

export function SectionTitleWithHelp({
  title,
  hoverHint,
}: {
  title: string;
  hoverHint: string;
}): JSX.Element {
  return (
    <div className="flex flex-wrap items-center gap-1.5">
      <h3 className="text-sm font-semibold text-slate-900">{title}</h3>
      <HoverHintWithGlyph size="sm" hoverHint={hoverHint} />
    </div>
  );
}

export const CLAMP_FORECAST_KIT_ITEMS_HOVER_HINT_SHELL_CLASS =
  "w-max max-w-[min(32rem,calc(100vw-2rem))] text-left";

export function formatClampForecastKitItemDescriptionVariant(
  hint: MrpSkuHoverHintFields | undefined,
): string {
  const parts = [hint?.description, hint?.variant].filter(
    (part) => part != null && String(part).trim() !== "",
  );
  return parts.length > 0 ? parts.join(" ") : "—";
}

export function ClampForecastKitItemsHoverHintBody({
  kitItems,
  skuHoverHints,
}: {
  kitItems: ClampForecastKitItemRow[];
  skuHoverHints: Record<string, MrpSkuHoverHintFields> | undefined;
}): ReactNode {
  if (kitItems.length === 0) {
    return "No kit items configured yet.";
  }
  return (
    <div className={CLAMP_FORECAST_KIT_ITEMS_HOVER_HINT_SHELL_CLASS}>
      <table className="w-full border-collapse text-[11px] leading-snug">
        <thead>
          <tr className="border-b border-slate-200 text-left text-slate-500">
            <th className="pr-3 pb-1 font-medium">SKU</th>
            <th className="pr-3 pb-1 font-medium">Description</th>
            <th className="pb-1 text-right font-medium">Per kit</th>
          </tr>
        </thead>
        <tbody>
          {kitItems.map((item) => (
            <tr key={item.id} className="border-b border-slate-100 last:border-0">
              <td className="py-1 pr-3 align-top font-mono text-slate-900">{item.component_sku}</td>
              <td className="max-w-[14rem] break-words py-1 pr-3 align-top text-slate-800">
                {formatClampForecastKitItemDescriptionVariant(skuHoverHints?.[item.component_sku])}
              </td>
              <td className="py-1 align-top tabular-nums text-right text-slate-800">
                {formatNumber(item.quantity_per_kit)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export interface ClampForecastTableProps {
  months: string[];
  categories: ClampForecastCategoryRow[];
  cellsIndex: Map<string, ClampForecastCellRow>;
  ghostsIndex: Map<string, ClampForecastGhostRow>;
  poLinksIndex: Map<number, ClampForecastPoLinkRow[]>;
  inventoryIndex: Map<string, ClampForecastInventoryRow[]>;
  kitItemsByCategory: Map<string, ClampForecastKitItemRow[]>;
  kitSkuHoverHints: Record<string, MrpSkuHoverHintFields> | undefined;
  derivedDemandIndex: Map<string, ClampForecastDerivedDemandRow[]>;
  derivedDemandSkusByCategory: Map<string, ClampForecastDerivedDemandSkuRow[]>;
  derivedDemandLoading: boolean;
  refreshingFrom: { categoryCode: string; fromMonth: string } | null;
  activeCell: { category_code: string; plan_month: string } | null;
  monthPoPlanByMonth: Map<string, ClampForecastMonthPoPlan>;
  hubWritesAllowed: boolean;
  draggingPoLinkId: number | null;
  poLinkDropHighlightMonth: string | null;
  poLinkDragSource: { categoryCode: string; planMonth: string } | null;
  poLinkDragSourceCellId: number | null;
  poLinkMovePending: boolean;
  onPoLinkDropHighlightMonth: (planMonth: string | null) => void;
  onDropPoLinkOnMonth: (linkId: number, cellId: number, targetPlanMonth: string) => void;
  onSelectCell: (next: { category_code: string; plan_month: string } | null) => void;
  onOpenMonthPo: (planMonth: string) => void;
}

export function ClampForecastTable({
  months,
  categories,
  cellsIndex,
  ghostsIndex,
  poLinksIndex,
  inventoryIndex,
  kitItemsByCategory,
  kitSkuHoverHints,
  derivedDemandIndex,
  derivedDemandSkusByCategory,
  derivedDemandLoading,
  refreshingFrom,
  activeCell,
  monthPoPlanByMonth,
  hubWritesAllowed,
  draggingPoLinkId,
  poLinkDropHighlightMonth,
  poLinkDragSource,
  poLinkDragSourceCellId,
  poLinkMovePending,
  onPoLinkDropHighlightMonth,
  onDropPoLinkOnMonth,
  onSelectCell,
  onOpenMonthPo,
}: ClampForecastTableProps): JSX.Element {
  if (categories.length === 0) {
    return (
      <p className="text-sm text-slate-500">
        No active clamp categories yet. Use the SQL settings (or the planned Clamp forecast settings page) to seed a category.
      </p>
    );
  }
  return (
    <div
      className="overflow-auto rounded-lg border border-slate-200"
      aria-live="polite"
    >
      <table className="min-w-full border-collapse text-sm">
        <thead>
          <tr className="border-b border-slate-200 bg-slate-50 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">
            <th className="sticky left-0 z-10 min-w-[14rem] bg-slate-50 px-3 py-2">Category</th>
            {months.map((month) => {
              const monthPlan = monthPoPlanByMonth.get(month);
              const monthPoReady = (monthPlan?.included_cell_count ?? 0) > 0;
              return (
              <th
                key={month}
                className={`whitespace-nowrap px-3 py-2 text-right align-bottom ${
                  isToday(month) ? "bg-brand-50 text-brand-800" : ""
                }`}
              >
                <div className="flex flex-col items-end gap-1">
                  <span>{formatMonth(month)}</span>
                  {hubWritesAllowed && monthPoReady ? (
                    <Button
                      type="button"
                      size="sm"
                      variant="secondary"
                      className="h-7 px-2 text-[11px] font-medium normal-case tracking-normal"
                      onClick={() => onOpenMonthPo(month)}
                    >
                      Create PO
                    </Button>
                  ) : null}
                </div>
              </th>
            );
            })}
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100">
          {categories.map((category) => {
            const kitItems = kitItemsByCategory.get(category.code) ?? [];
            const derivedSkuRows = derivedDemandSkusByCategory.get(category.code) ?? [];
            const supplyPipeline = buildClampSupplyPipeline(category, derivedSkuRows);
            const rowDerivedDemandLoading = !category.is_kit && derivedDemandLoading;
            return (
              <Fragment key={category.code}>
                <tr className="bg-white">
                  <th
                    scope="row"
                    className="sticky left-0 z-10 bg-white px-3 py-2 align-top text-left"
                  >
                    <div className="flex flex-col">
                      <span className="text-sm font-semibold text-slate-900">{category.display_name}</span>
                      <span className="font-mono text-xs text-slate-500">
                        {category.is_kit ? (
                          `${kitItems.length} kit component${kitItems.length === 1 ? "" : "s"}`
                        ) : category.fg_sku != null ? (
                          <MrpSkuDetailLink
                            sku={category.fg_sku}
                            className="font-mono text-xs text-sky-800 decoration-sky-300"
                          />
                        ) : (
                          "—"
                        )}
                      </span>
                      {supplyPipeline != null ? (
                        <ClampPipelineOnHandStrip
                          pipeline={supplyPipeline}
                          inventoryIndex={inventoryIndex}
                        />
                      ) : category.is_kit ? null : category.fg_sku != null ? (
                        <InventoryStripCompact rows={lookupInventoryRows(inventoryIndex, category.fg_sku)} />
                      ) : null}
                      {category.is_kit ? (
                        <HoverHintContent
                          hoverHint={
                            <ClampForecastKitItemsHoverHintBody
                              kitItems={kitItems}
                              skuHoverHints={kitSkuHoverHints}
                            />
                          }
                          align="start"
                        >
                          <span className="mt-0.5 inline-flex items-center gap-1 text-xs text-slate-500 underline-offset-2 hover:underline">
                            view kit items
                          </span>
                        </HoverHintContent>
                      ) : null}
                      {category.is_kit ? (
                        <HoverHintContent
                          hoverHint="The middle MRP-rolled-forward line is skipped for kit categories in v1 — kit components rarely drive build-plan demand directly."
                          align="start"
                        >
                          <span className="mt-0.5 inline-flex items-center text-[11px] text-slate-400">
                            MRP roll: n/a (kit)
                          </span>
                        </HoverHintContent>
                      ) : null}
                    </div>
                  </th>
                  {months.map((month) => {
                    const cell = cellsIndex.get(cellKey(category.code, month));
                    const ghost = ghostsIndex.get(cellKey(category.code, month));
                    const poLinks = cell != null ? poLinksIndex.get(cell.id) : undefined;
                    const ordered = orderedForForecastCell(
                      category,
                      kitItems,
                      cell?.forecast_qty ?? 0,
                      poLinks,
                    );
                    const derivedRows = category.is_kit
                      ? undefined
                      : derivedDemandIndex.get(cellKey(category.code, month));
                    const rolledQty = sumRolledQtyAcrossLocations(derivedRows);
                    const isActive =
                      activeCell != null &&
                      activeCell.category_code === category.code &&
                      activeCell.plan_month === month;
                    const isPoLinkDropTarget =
                      hubWritesAllowed &&
                      !poLinkMovePending &&
                      poLinkDragSource != null &&
                      draggingPoLinkId != null &&
                      poLinkDragSource.categoryCode === category.code &&
                      poLinkDragSource.planMonth !== month &&
                      poLinkDragSourceCellId != null;
                    const isPoLinkDropHighlighted = isPoLinkDropTarget && poLinkDropHighlightMonth === month;
                    const cellDerivedDemandRefreshing =
                      rowDerivedDemandLoading ||
                      isClampForecastRollForwardRefreshing(refreshingFrom, category.code, month);
                    return (
                      <td
                        key={month}
                        className={`whitespace-nowrap px-2 py-1.5 text-right align-top ${
                          isToday(month) ? "bg-brand-50/40" : ""
                        } ${isActive ? "outline outline-2 outline-brand-500" : ""} ${
                          isPoLinkDropHighlighted ? "bg-emerald-50 ring-2 ring-inset ring-emerald-400" : ""
                        }`}
                        onDragOver={(event) => {
                          if (!isPoLinkDropTarget) return;
                          event.preventDefault();
                          event.dataTransfer.dropEffect = "move";
                          onPoLinkDropHighlightMonth(month);
                        }}
                        onDragLeave={() => {
                          if (poLinkDropHighlightMonth === month) {
                            onPoLinkDropHighlightMonth(null);
                          }
                        }}
                        onDrop={(event) => {
                          if (!isPoLinkDropTarget || poLinkDragSourceCellId == null || draggingPoLinkId == null) {
                            return;
                          }
                          event.preventDefault();
                          event.stopPropagation();
                          onDropPoLinkOnMonth(draggingPoLinkId, poLinkDragSourceCellId, month);
                          onPoLinkDropHighlightMonth(null);
                        }}
                      >
                        <button
                          type="button"
                          data-clamp-forecast-cell={cellKey(category.code, month)}
                          onClick={() => onSelectCell({ category_code: category.code, plan_month: month })}
                          className="block w-full rounded px-1.5 py-1 text-right hover:bg-slate-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500"
                        >
                          <ForecastQtyCellLine
                            cell={cell}
                            ghost={ghost}
                            isRefreshing={cellDerivedDemandRefreshing}
                          />
                          {category.is_kit ? null : (
                            <DerivedDemandCellLine
                              rows={derivedRows}
                              rolledQty={rolledQty}
                              supplyPipeline={supplyPipeline}
                              forecastCell={cell}
                              poLinks={poLinks}
                              isRefreshing={cellDerivedDemandRefreshing}
                            />
                          )}
                          <LinkedPurchaseOrdersCellLine
                            poLinks={poLinks}
                            forecastQty={cell?.forecast_qty ?? 0}
                            ordered={ordered}
                            isRefreshing={cellDerivedDemandRefreshing}
                          />
                        </button>
                      </td>
                    );
                  })}
                </tr>
              </Fragment>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

export function InventoryStripCompact({ rows }: { rows: ClampForecastInventoryRow[] }): JSX.Element | null {
  if (rows.length === 0) return null;
  return (
    <div className="mt-1 flex flex-wrap gap-1 text-[11px] text-slate-500">
      {rows.map((row) => (
        <span
          key={`${row.sku}::${row.location_code}`}
          className="inline-flex items-center gap-1 rounded bg-slate-100 px-1.5 py-0.5"
        >
          <span className="uppercase tracking-wide text-slate-500">{row.location_code}</span>
          <span className="tabular-nums text-slate-700">{formatNumber(inventoryPhysicalOnHand(row))}</span>
        </span>
      ))}
    </div>
  );
}

export function ClampPipelineOnHandStrip({
  pipeline,
  inventoryIndex,
}: {
  pipeline: ClampSupplyPipeline;
  inventoryIndex: Map<string, ClampForecastInventoryRow[]>;
}): JSX.Element {
  const skus = [pipeline.raw, pipeline.qc, pipeline.tla].filter(
    (sku): sku is string => sku != null && sku.trim() !== "",
  );
  const byLocation = new Map<string, { total: number; parts: { sku: string; qty: number }[] }>();
  for (const sku of skus) {
    for (const row of lookupInventoryRows(inventoryIndex, sku)) {
      const location = row.location_code.toLowerCase();
      const onHand = inventoryPhysicalOnHand(row);
      const existing = byLocation.get(location) ?? { total: 0, parts: [] };
      existing.total += onHand;
      existing.parts.push({ sku, qty: onHand });
      byLocation.set(location, existing);
    }
  }
  if (byLocation.size === 0) return <></>;
  return (
    <div className="mt-1 space-y-1 text-[10px] leading-snug text-slate-500">
      <div className="font-medium uppercase tracking-wide text-slate-400">On hand now</div>
      <div className="flex flex-wrap gap-1">
        {[...byLocation.entries()]
          .sort(([a], [b]) => a.localeCompare(b))
          .map(([location, entry]) => {
            const breakoutHint = entry.parts
              .map((part) => `${pipelineStageLabel(part.sku, pipeline)} ${formatNumber(part.qty)}`)
              .join(" · ");
            return (
              <HoverHintContent key={location} hoverHint={breakoutHint} align="start">
                <span className="inline-flex cursor-default items-center gap-1 rounded bg-slate-100 px-1.5 py-0.5">
                  <span className="uppercase tracking-wide text-slate-500">{location}</span>
                  <span className="tabular-nums font-medium text-slate-700">{formatNumber(entry.total)}</span>
                </span>
              </HoverHintContent>
            );
          })}
      </div>
    </div>
  );
}

/** Green when linked PO qty matches forecast; red when forecast is set but qty differs. */
export function linkedPoLineTone(
  forecastQty: number,
  ordered: number,
  hasPoLinks: boolean,
): "neutral" | "match" | "mismatch" {
  if (forecastQty <= 0) return "neutral";
  if (!hasPoLinks || ordered !== forecastQty) return "mismatch";
  return "match";
}

export const LINKED_PO_LINE_TONE_CLASS: Record<ReturnType<typeof linkedPoLineTone>, string> = {
  neutral: "text-slate-500",
  match: "text-emerald-700",
  mismatch: "text-rose-700",
};

/**
 * Bottom line of a grid cell: total ordered quantity on linked POs. Hover shows each
 * PO number with received and ordered quantities.
 */
export function LinkedPurchaseOrdersCellLine({
  poLinks,
  forecastQty,
  ordered,
  isRefreshing = false,
}: {
  poLinks: ClampForecastPoLinkRow[] | undefined;
  forecastQty: number;
  ordered: number;
  isRefreshing?: boolean;
}): JSX.Element {
  const hasPoLinks = poLinks != null && poLinks.length > 0;
  const tone = linkedPoLineTone(forecastQty, ordered, hasPoLinks);
  const toneClass = LINKED_PO_LINE_TONE_CLASS[tone];

  if (!hasPoLinks) {
    return (
      <div
        className={cn(
          "tabular-nums text-[11px] leading-tight",
          toneClass,
          isRefreshing && GRID_REFRESHING_NUMBER_CLASS,
        )}
      >
        no PO
      </div>
    );
  }

  const hoverBody = (
    <span className="block space-y-1.5 text-xs">
      <span className="block font-semibold text-slate-800">Linked purchase orders</span>
      <span className="block space-y-1">
        {poLinks.map((link) => (
          <span key={link.id} className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-0.5">
            <span className="font-mono text-slate-700">
              {link.mv_number ?? `PO #${link.purchase_order_id}`}
            </span>
            <span className="tabular-nums text-slate-600">
              {formatNumber(link.quantity_received)} received · {formatNumber(link.quantity_ordered)} qty ·{" "}
              {formatClampForecastUnitPrice(link.unit_price)}
            </span>
          </span>
        ))}
      </span>
    </span>
  );

  return (
    <HoverHintContent hoverHint={hoverBody} align="start" block>
      <span
        className={cn(
          "block tabular-nums text-[11px] leading-tight",
          toneClass,
          isRefreshing && GRID_REFRESHING_NUMBER_CLASS,
        )}
      >
        {formatNumber(ordered)}
      </span>
    </HoverHintContent>
  );
}

/**
 * Top line of a grid cell: saved forecast quantity plus optional what-if adjustment
 * (purple when positive, amber when negative).
 */
export function ForecastQtyCellLine({
  cell,
  ghost,
  isRefreshing = false,
}: {
  cell: ClampForecastCellRow | undefined;
  ghost: ClampForecastGhostRow | undefined;
  isRefreshing?: boolean;
}): JSX.Element {
  const forecastQty = cell?.forecast_qty ?? 0;
  const ghostQty = ghost?.ghost_qty ?? 0;
  const hasForecast = cell != null && forecastQty > 0;
  const hasGhost = ghost != null && ghostQty !== 0;

  const line = (
    <div
      className={cn(
        "font-semibold tabular-nums text-slate-900",
        isRefreshing && GRID_REFRESHING_NUMBER_CLASS,
      )}
    >
      {hasForecast ? formatNumber(forecastQty) : hasGhost ? "0" : "—"}
      {hasGhost ? (
        <span className={ghostQty > 0 ? "text-violet-700" : "text-amber-800"}>
          {ghostQty > 0 ? "+" : ""}
          {formatNumber(ghostQty)}
        </span>
      ) : null}
    </div>
  );

  if (!hasGhost || ghost == null) return line;

  const positive = ghostQty > 0;
  const arrivalLabel =
    ghost.expected_arrival_date != null && ghost.expected_arrival_date.trim() !== ""
      ? ` Expected arrival: ${ghost.expected_arrival_date}.`
      : "";
  const hoverHint =
    `What-if adjustment for ${formatMonth(ghost.plan_month)}. ` +
    `${positive ? "Adds" : "Subtracts"} ${formatNumber(Math.abs(ghostQty))} on top of the saved forecast.` +
    `${arrivalLabel}${ghost.notes?.trim() ? ` Note: ${ghost.notes.trim()}` : ""}`;

  return (
    <HoverHintContent hoverHint={hoverHint} align="start" block>
      {line}
    </HoverHintContent>
  );
}

export function DerivedDemandCellLine({
  rows,
  rolledQty,
  supplyPipeline,
  forecastCell,
  poLinks,
  isRefreshing = false,
}: {
  rows: ClampForecastDerivedDemandRow[] | undefined;
  rolledQty: number | null;
  supplyPipeline: ClampSupplyPipeline | null;
  forecastCell: ClampForecastCellRow | null | undefined;
  poLinks: ClampForecastPoLinkRow[] | undefined;
  isRefreshing?: boolean;
}): JSX.Element {
  if (rows == null || rows.length === 0 || rolledQty == null) {
    return (
      <div
        className={cn(
          "tabular-nums text-[11px] leading-tight text-slate-400",
          isRefreshing && GRID_REFRESHING_NUMBER_CLASS,
        )}
      >
        —
      </div>
    );
  }
  const negative = rolledQty < 0;
  const sortedRows = [...rows].sort((a, b) => a.location_code.localeCompare(b.location_code));
  const planMonth = sortedRows[0]?.plan_month ?? "";
  const sampleNextDate = sortedRows.find((row) => row.next_component_order_date != null)?.next_component_order_date;
  const forecastExpectedArrival =
    forecastCell != null && forecastCell.forecast_qty > 0
      ? resolveClampForecastExpectedArrivalDate(forecastCell, poLinks ?? [])
      : null;
  const tlaDemandTotal = sortedRows.reduce((sum, row) => sum + row.demand, 0);
  const tlaDemandByLocation = sortedRows
    .filter((row) => row.demand !== 0)
    .map((row) => `${row.location_code.toUpperCase()} ${formatNumber(row.demand)}`)
    .join(" · ");
  const hasPipelineBreakdown = sortedRows.some(
    (row) => row.pipeline_breakdown != null && row.pipeline_breakdown.length > 0,
  );

  const hoverBody = (
    <span className="block space-y-2 text-xs">
      <span className="block">
        <span className="block font-semibold text-slate-800">
          {formatMonth(planMonth)} rolled on-hand
          {hasPipelineBreakdown ? " (raw + QC + TLA)" : null}
        </span>
        {hasPipelineBreakdown && tlaDemandTotal > 0 ? (
          <span className="mt-0.5 block text-[11px] text-slate-500">
            Build-plan demand on TLA (by location): {formatNumber(tlaDemandTotal)}
            {tlaDemandByLocation.length > 0 ? ` — ${tlaDemandByLocation}` : null}
          </span>
        ) : null}
      </span>
      <PipelineRollBreakdownTable rows={sortedRows} supplyPipeline={supplyPipeline} />
      <span className="flex justify-between gap-3 border-t border-slate-200 pt-1.5 font-mono text-sm">
        <span className="font-semibold text-slate-700">All locations</span>
        <span className={rolledQty < 0 ? "font-semibold text-rose-700" : "font-semibold text-slate-800"}>
          {formatNumber(rolledQty)}
        </span>
      </span>
      {forecastExpectedArrival != null ? (
        <span className="block text-[11px] text-slate-500">
          Expected arrival: {forecastExpectedArrival}
          {forecastCell != null && forecastCell.forecast_qty > 0
            ? ` (${formatNumber(forecastCell.forecast_qty)} forecast)`
            : null}
        </span>
      ) : sampleNextDate != null ? (
        <span className="block text-[11px] text-slate-500">Next component order date: {sampleNextDate}</span>
      ) : null}
    </span>
  );
  return (
    <HoverHintContent hoverHint={hoverBody} align="start" block>
      <span
        className={cn(
          "block tabular-nums text-[11px] leading-tight",
          negative ? "font-medium text-rose-600" : "text-slate-500",
          isRefreshing && GRID_REFRESHING_NUMBER_CLASS,
        )}
      >
        {formatSignedRolledQty(rolledQty)}
      </span>
    </HoverHintContent>
  );
}

export interface ClampForecastSidePanelProps {
  category: ClampForecastCategoryRow;
  planMonth: string;
  cell: ClampForecastCellRow | null;
  ghost: ClampForecastGhostRow | null;
  poLinks: ClampForecastPoLinkRow[];
  kitItems: ClampForecastKitItemRow[];
  derivedDemandRows: ClampForecastDerivedDemandRow[];
  hubWritesAllowed: boolean;
  draggingPoLinkId: number | null;
  poLinkMovePending: boolean;
  onDragPoLinkStart: (linkId: number) => void;
  onDragPoLinkEnd: () => void;
  onClose: () => void;
  onCellSaved: (cell: ClampForecastCellRow) => void;
  onCellCleared: (categoryCode: string, planMonth: string) => void;
  onGhostSaved: (ghost: ClampForecastGhostRow | null, categoryCode: string, planMonth: string) => void;
  onGridStale: () => void;
  onError: (message: string) => void;
  onSuccess: (message: string) => void;
}

/** Prefill the callout date field for new cells only; persisted only after Save forecast. */
export function expectedArrivalInputDefault(cell: ClampForecastCellRow | null, planMonth: string): string {
  if (cell == null) {
    return defaultClampForecastExpectedArrivalDate(planMonth);
  }
  return cell.expected_arrival_date ?? "";
}

export function ghostExpectedArrivalInputDefault(
  ghost: ClampForecastGhostRow | null,
  cell: ClampForecastCellRow | null,
  planMonth: string,
  poLinks: ClampForecastPoLinkRow[],
): string {
  if (ghost?.expected_arrival_date != null && ghost.expected_arrival_date.trim() !== "") {
    return ghost.expected_arrival_date;
  }
  if (cell != null) {
    const fromCell = resolveClampForecastExpectedArrivalDate(
      cell,
      poLinks.map((link) => ({ expected_arrival: link.expected_arrival })),
    );
    if (fromCell != null) return fromCell;
  }
  return defaultClampForecastExpectedArrivalDate(planMonth);
}

export function ClampForecastSidePanel({
  category,
  planMonth,
  cell,
  ghost,
  poLinks,
  kitItems,
  derivedDemandRows: _derivedDemandRows,
  hubWritesAllowed,
  draggingPoLinkId,
  poLinkMovePending,
  onDragPoLinkStart,
  onDragPoLinkEnd,
  onClose,
  onCellSaved,
  onCellCleared,
  onGhostSaved,
  onGridStale,
  onError,
  onSuccess,
}: ClampForecastSidePanelProps): JSX.Element {
  const queryClient = useQueryClient();
  const forecastQtyInputRef = useRef<HTMLInputElement>(null);
  const [forecastQtyInput, setForecastQtyInput] = useState<string>(
    cell != null ? String(cell.forecast_qty) : "0",
  );
  const [expectedArrivalInput, setExpectedArrivalInput] = useState<string>(() =>
    expectedArrivalInputDefault(cell, planMonth),
  );
  const [revisionNote, setRevisionNote] = useState<string>("");
  const [ghostQtyInput, setGhostQtyInput] = useState<string>(ghost != null ? String(ghost.ghost_qty) : "");
  const [ghostExpectedArrivalInput, setGhostExpectedArrivalInput] = useState<string>(() =>
    ghostExpectedArrivalInputDefault(ghost, cell, planMonth, poLinks),
  );
  const [ghostNotesInput, setGhostNotesInput] = useState<string>(ghost?.notes ?? "");
  const [showCreatePo, setShowCreatePo] = useState<boolean>(false);
  const [poActionSelection, setPoActionSelection] = useState<string>("new");

  const addToPoOptionsQuery = useQuery({
    queryKey: ["mrp", "clamp-forecast", "add-to-po-options"],
    queryFn: () =>
      apiFetch<{ options: ClampForecastAddToPoOption[] }>("/api/mrp/clamp-forecast/add-to-po-options"),
    staleTime: 60_000,
  });
  const addToPoOptions = addToPoOptionsQuery.data?.options ?? [];

  useEffect(() => {
    setForecastQtyInput(cell != null ? String(cell.forecast_qty) : "0");
    setExpectedArrivalInput(expectedArrivalInputDefault(cell, planMonth));
    setRevisionNote("");
    setGhostQtyInput(ghost != null ? String(ghost.ghost_qty) : "");
    setGhostExpectedArrivalInput(ghostExpectedArrivalInputDefault(ghost, cell, planMonth, poLinks));
    setGhostNotesInput(ghost?.notes ?? "");
    setShowCreatePo(false);
    setPoActionSelection("new");
  }, [cell?.id, ghost?.id, planMonth, category.code, poLinks]);

  const upsertMutation = useMutation({
    mutationFn: async () => {
      const parsed = Number.parseInt(forecastQtyInput, 10);
      if (!Number.isFinite(parsed) || parsed < 0) {
        throw new Error("Forecast must be a non-negative whole number.");
      }
      return apiFetch<{ cell: ClampForecastCellRow }>("/api/mrp/clamp-forecast/cells", {
        method: "PUT",
        body: {
          category_code: category.code,
          plan_month: planMonth,
          forecast_qty: parsed,
          expected_arrival_date:
            expectedArrivalInput.trim() === "" ? null : expectedArrivalInput.trim(),
          revision_note: revisionNote.trim() === "" ? null : revisionNote.trim(),
        },
      });
    },
    onSuccess: (data) => {
      onSuccess("Forecast saved.");
      setRevisionNote("");
      onCellSaved(data.cell);
      onClose();
    },
    onError: (error) => onError(describeApiError(error)),
  });

  const clearMutation = useMutation({
    mutationFn: async () =>
      apiFetch<{ deleted: boolean }>("/api/mrp/clamp-forecast/cells", {
        method: "DELETE",
        query: { category_code: category.code, plan_month: planMonth },
      }),
    onMutate: () => {
      onCellCleared(category.code, planMonth);
    },
    onSuccess: () => {
      onSuccess("Forecast cell cleared.");
      onClose();
    },
    onError: (error) => {
      onGridStale();
      onError(describeApiError(error));
    },
  });

  const ghostUpsertMutation = useMutation({
    mutationFn: async () => {
      const parsed = Number.parseInt(ghostQtyInput, 10);
      if (!Number.isFinite(parsed)) {
        throw new Error("Ghost quantity must be a whole number.");
      }
      if (parsed !== 0 && ghostExpectedArrivalInput.trim() === "") {
        throw new Error("Expected arrival date is required when ghost quantity is non-zero.");
      }
      return apiFetch<{ ghost: ClampForecastGhostRow | null; deleted: boolean }>(
        "/api/mrp/clamp-forecast/ghosts",
        {
          method: "PUT",
          body: {
            category_code: category.code,
            plan_month: planMonth,
            ghost_qty: parsed,
            expected_arrival_date:
              ghostExpectedArrivalInput.trim() === "" ? null : ghostExpectedArrivalInput.trim(),
            notes: ghostNotesInput.trim() === "" ? null : ghostNotesInput.trim(),
          },
        },
      );
    },
    onSuccess: (data) => {
      onGhostSaved(data.ghost, category.code, planMonth);
      onSuccess(data.deleted ? "Ghost shipment adjustment cleared." : "Ghost shipment adjustment saved.");
      onClose();
    },
    onError: (error) => onError(describeApiError(error)),
  });

  const ghostClearMutation = useMutation({
    mutationFn: async () =>
      apiFetch<{ deleted: boolean }>("/api/mrp/clamp-forecast/ghosts", {
        method: "DELETE",
        query: { category_code: category.code, plan_month: planMonth },
      }),
    onSuccess: () => {
      setGhostQtyInput("");
      setGhostExpectedArrivalInput(ghostExpectedArrivalInputDefault(null, cell, planMonth, poLinks));
      setGhostNotesInput("");
      onGhostSaved(null, category.code, planMonth);
      onSuccess("Ghost shipment adjustment cleared.");
    },
    onError: (error) => onError(describeApiError(error)),
  });

  const revisionsQuery = useQuery({
    queryKey: ["mrp", "clamp-forecast", "revisions", cell?.id ?? null],
    enabled: cell != null,
    queryFn: () =>
      apiFetch<{ revisions: ClampForecastRevisionRow[] }>(
        `/api/mrp/clamp-forecast/cells/${cell!.id}/revisions`,
      ),
  });

  const writeDisabled = !hubWritesAllowed;

  const ghostSaveDisabled =
    writeDisabled ||
    ghostQtyInput.trim() === "" ||
    ghostExpectedArrivalInput.trim() === "" ||
    ghostUpsertMutation.isPending ||
    ghostClearMutation.isPending;

  const remainingToAllocate = useMemo(
    () =>
      computeRemainingClampForecastAllocation({
        forecast_qty: cell?.forecast_qty ?? 0,
        category,
        kitItems,
        linkedLines: poLinks.map((link) => ({
          sku: link.sku,
          quantity_ordered: link.quantity_ordered,
        })),
      }),
    [cell?.forecast_qty, category, kitItems, poLinks],
  );

  useLayoutEffect(() => {
    if (writeDisabled) return;
    const input = forecastQtyInputRef.current;
    if (input == null) return;
    input.focus();
    input.select();
  }, [category.code, planMonth, cell?.id, writeDisabled]);

  const linkPurchaseOrderMutation = useMutation({
    mutationFn: async (purchaseOrderId: number) => {
      if (cell == null) {
        throw new Error("Save this forecast cell before linking a purchase order.");
      }
      return apiFetch<{
        forecast_po_links_created: number;
        already_linked: boolean;
        po_lines_added?: number;
      }>(
        `/api/mrp/clamp-forecast/cells/${cell.id}/link-purchase-order`,
        {
          method: "POST",
          body: { purchase_order_id: purchaseOrderId },
        },
      );
    },
    onSuccess: async (data) => {
      if (data.already_linked) {
        onSuccess("That purchase order is already linked to this forecast cell.");
      } else {
        const added =
          data.po_lines_added != null && data.po_lines_added > 0
            ? ` Added ${data.po_lines_added} line${data.po_lines_added === 1 ? "" : "s"} to the purchase order.`
            : "";
        onSuccess(
          `Linked ${data.forecast_po_links_created} purchase order line${
            data.forecast_po_links_created === 1 ? "" : "s"
          } to this forecast cell.${added}`,
        );
      }
      void queryClient.invalidateQueries({ queryKey: ["mrp", "clamp-forecast", "add-to-po-options"] });
      onGridStale();
    },
    onError: (error) => onError(describeApiError(error)),
  });

  const poActionDisabled =
    writeDisabled ||
    cell == null ||
    cell.forecast_qty <= 0 ||
    remainingToAllocate <= 0 ||
    linkPurchaseOrderMutation.isPending ||
    addToPoOptionsQuery.isLoading;

  const handlePoAction = (): void => {
    if (poActionSelection === "new") {
      setShowCreatePo(true);
      return;
    }
    linkPurchaseOrderMutation.mutate(Number.parseInt(poActionSelection, 10));
  };

  return (
    <div className="flex min-w-0 flex-col">
      <div className="sticky top-0 z-10 shrink-0 flex flex-wrap items-baseline justify-between gap-2 border-b border-slate-200 bg-white px-4 py-3">
        <h2 className="text-base font-semibold text-slate-900">
          {category.display_name} — {formatMonth(planMonth)}
        </h2>
        <Button variant="ghost" size="sm" onClick={onClose} aria-label="Close side panel">
          Close
        </Button>
      </div>
      <div className="space-y-4 px-4 py-4">
        <section className="space-y-2">
          <div className="grid gap-3 sm:grid-cols-[1fr_auto_auto]">
            <div>
              <Label htmlFor="clamp-forecast-qty">Forecast quantity</Label>
              <Input
                ref={forecastQtyInputRef}
                id="clamp-forecast-qty"
                type="number"
                min={0}
                value={forecastQtyInput}
                onChange={(event) => setForecastQtyInput(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key !== "Enter" || writeDisabled || upsertMutation.isPending) return;
                  event.preventDefault();
                  upsertMutation.mutate();
                }}
                disabled={writeDisabled}
              />
            </div>
            <div className="self-end">
              <Button
                onClick={() => upsertMutation.mutate()}
                disabled={writeDisabled}
                isLoading={upsertMutation.isPending}
              >
                Save forecast
              </Button>
            </div>
            <div className="self-end">
              <Button
                variant="secondary"
                onClick={() => clearMutation.mutate()}
                disabled={writeDisabled || cell == null}
                isLoading={clearMutation.isPending}
              >
                Clear cell
              </Button>
            </div>
          </div>
          <div>
            <FieldLabelWithHelp
              htmlFor="clamp-forecast-expected-arrival"
              label="Expected arrival date"
              hoverHint={`New cells prefill to 60 days after the end of ${formatMonth(planMonth)}. An existing cell with no saved date stays blank until you set one. Saved with the forecast and copied to the PO when you create one. The middle-line arrival math uses only a saved date or linked PO dates — not the new-cell default.`}
            />
            <DateInput
              id="clamp-forecast-expected-arrival"
              value={expectedArrivalInput}
              disabled={writeDisabled}
              onChange={(event) => setExpectedArrivalInput(event.target.value)}
              className="mt-1.5"
            />
          </div>
          <div>
            <Label htmlFor="clamp-forecast-revision-note">Revision note (optional)</Label>
            <Input
              id="clamp-forecast-revision-note"
              value={revisionNote}
              onChange={(event) => setRevisionNote(event.target.value)}
              placeholder="Why this revision? (saved alongside the quantity change)"
              disabled={writeDisabled}
            />
          </div>
        </section>

        <section className="space-y-2 border-t border-slate-200 pt-4">
          <SectionTitleWithHelp
            title="Ghost shipment adjustment"
            hoverHint={
              category.is_kit
                ? "Signed what-if delta for this plan month on a kit row. Positive values simulate extra WYJ kit shipments; negative values simulate fewer. Set the expected arrival date for when this scenario should land. Kit categories have no MRP roll-forward line. The saved forecast quantity above is unchanged."
                : "Signed what-if delta for this plan month. Positive values simulate extra WYJ arrivals; negative values simulate fewer. Set the ghost expected arrival date separately from the forecast — the middle roll-forward line changes on that month. The saved forecast quantity above is unchanged."
            }
          />
          <form
            className="space-y-2"
            onSubmit={(event) => {
              event.preventDefault();
              if (ghostSaveDisabled) return;
              ghostUpsertMutation.mutate();
            }}
          >
            <div className="grid gap-3 sm:grid-cols-[1fr_auto_auto]">
              <div>
                <Label htmlFor="clamp-forecast-ghost-qty">Ghost quantity</Label>
                <Input
                  id="clamp-forecast-ghost-qty"
                  type="number"
                  value={ghostQtyInput}
                  placeholder="0 to clear"
                  disabled={writeDisabled || ghostUpsertMutation.isPending || ghostClearMutation.isPending}
                  onChange={(event) => setGhostQtyInput(event.target.value)}
                />
              </div>
              <div className="self-end">
                <Button
                  type="submit"
                  disabled={ghostSaveDisabled}
                  isLoading={ghostUpsertMutation.isPending}
                >
                  Save ghost
                </Button>
              </div>
              <div className="self-end">
                <Button
                  type="button"
                  variant="secondary"
                  onClick={() => ghostClearMutation.mutate()}
                  disabled={writeDisabled || ghost == null || ghostClearMutation.isPending || ghostUpsertMutation.isPending}
                  isLoading={ghostClearMutation.isPending}
                >
                  Clear ghost
                </Button>
              </div>
            </div>
            <div>
              <FieldLabelWithHelp
                htmlFor="clamp-forecast-ghost-expected-arrival"
                label="Expected arrival date"
                hoverHint={`When this ghost adjustment lands in middle-line roll-forward math. Prefills from the saved ghost date, then the forecast cell or linked PO date, then ${formatMonth(planMonth)} + 60 days. Independent of the forecast expected arrival field above.`}
              />
              <DateInput
                id="clamp-forecast-ghost-expected-arrival"
                value={ghostExpectedArrivalInput}
                disabled={writeDisabled || ghostUpsertMutation.isPending || ghostClearMutation.isPending}
                onChange={(event) => setGhostExpectedArrivalInput(event.target.value)}
                className="mt-1.5"
              />
            </div>
            <div>
              <Label htmlFor="clamp-forecast-ghost-notes">Ghost note (optional)</Label>
              <Input
                id="clamp-forecast-ghost-notes"
                value={ghostNotesInput}
                disabled={writeDisabled || ghostUpsertMutation.isPending || ghostClearMutation.isPending}
                onChange={(event) => setGhostNotesInput(event.target.value)}
                placeholder="Why this scenario exists"
              />
            </div>
          </form>
        </section>

        <section className="space-y-2">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <SectionTitleWithHelp
              title="Linked purchase orders"
              hoverHint={`Choose New PO to create a hub purchase order for the remaining unallocated forecast quantity, or pick an open Beijing YuanTang International Trade PO and click Add to PO. Drag a linked PO card onto another month column in this category row to reassign it. Save a positive forecast quantity first.`}
            />
            <div className="flex flex-wrap items-center gap-2">
              <select
                id="clamp-forecast-po-action"
                className="h-9 min-w-[20rem] max-w-full flex-1 rounded-md border border-slate-300 bg-white px-2 text-sm text-slate-900 disabled:cursor-not-allowed disabled:bg-slate-50"
                value={poActionSelection}
                disabled={poActionDisabled}
                onChange={(event) => {
                  setPoActionSelection(event.target.value);
                  if (event.target.value !== "new") {
                    setShowCreatePo(false);
                  }
                }}
              >
                <option value="new">New PO</option>
                {addToPoOptions.map((option) => {
                  const label = formatClampForecastAddToPoOptionLabel(option);
                  const fullInstructions = option.special_instructions?.trim() ?? "";
                  return (
                    <option
                      key={option.purchase_order_id}
                      value={String(option.purchase_order_id)}
                      title={
                        fullInstructions !== "" && fullInstructions !== label
                          ? `${option.mv_number ?? `PO #${option.purchase_order_id}`}: ${fullInstructions}`
                          : undefined
                      }
                    >
                      {label}
                    </option>
                  );
                })}
              </select>
              <Button
                size="sm"
                variant="secondary"
                onClick={handlePoAction}
                disabled={poActionDisabled}
                isLoading={linkPurchaseOrderMutation.isPending}
              >
                {poActionSelection === "new" ? "Create PO" : "Add to PO"}
              </Button>
            </div>
          </div>
          {cell == null || cell.forecast_qty <= 0 ? (
            <p className="text-xs text-slate-500">Save a positive forecast quantity first.</p>
          ) : remainingToAllocate <= 0 ? (
            <p className="text-xs text-slate-500">
              Forecast is fully allocated on linked purchase orders. Use Link PO to attach another existing order, or raise the forecast quantity.
            </p>
          ) : remainingToAllocate < cell.forecast_qty ? (
            <p className="text-xs text-slate-600">
              {formatNumber(remainingToAllocate)} of {formatNumber(cell.forecast_qty)} forecast still to allocate on purchase orders.
            </p>
          ) : null}
          {poLinks.length === 0 ? (
            <p className="text-xs text-slate-500">No POs linked to this forecast cell yet.</p>
          ) : (
            <ul className="space-y-1 text-sm">
              {poLinks.map((link) => (
                <li
                  key={link.id}
                  draggable={hubWritesAllowed && !poLinkMovePending}
                  onDragStart={(event) => {
                    event.stopPropagation();
                    event.dataTransfer.effectAllowed = "move";
                    event.dataTransfer.setData("text/plain", String(link.id));
                    onDragPoLinkStart(link.id);
                  }}
                  onDragEnd={onDragPoLinkEnd}
                  className={cn(
                    "flex min-w-0 flex-col gap-0.5 rounded border border-slate-200 bg-white px-2 py-1",
                    hubWritesAllowed && !poLinkMovePending
                      ? "cursor-grab active:cursor-grabbing"
                      : "",
                    draggingPoLinkId === link.id ? "border-brand-300 bg-brand-50/60 opacity-80" : "",
                  )}
                >
                  <div className="flex min-w-0 flex-wrap items-center gap-2">
                    <ClampForecastPoDocumentLink
                      mvNumber={link.mv_number}
                      purchaseOrderId={link.purchase_order_id}
                      purchaseOrderMvId={link.purchase_order_mv_id}
                    />
                    <Link
                      to={buildMrpSkuDetailPath(link.sku)}
                      className="font-mono text-slate-700 hover:underline"
                    >
                      {link.sku}
                    </Link>
                    <span className="text-xs tabular-nums text-slate-600">
                      {formatNumber(link.quantity_received)}/{formatNumber(link.quantity_ordered)}
                    </span>
                    <span className="text-xs tabular-nums text-slate-600">
                      {formatClampForecastUnitPrice(link.unit_price)}
                    </span>
                    {link.expected_arrival != null ? (
                      <Link
                        to={buildMrpIncomingPartsPath({ po: link.mv_number })}
                        className="text-xs text-brand-700 hover:underline"
                      >
                        Expected {link.expected_arrival}
                      </Link>
                    ) : null}
                  </div>
                  {link.expected_qc_complete_date != null ? (
                    <div className="flex flex-wrap items-center gap-3 text-[11px] text-slate-500">
                      <span>QC by {link.expected_qc_complete_date}</span>
                    </div>
                  ) : null}
                  {clampForecastPoLinkShowsSpecialInstructions(link, poLinks) ? (
                    <p className="whitespace-pre-wrap break-words text-[11px] leading-snug text-slate-600">
                      {link.special_instructions?.trim()}
                    </p>
                  ) : null}
                </li>
              ))}
            </ul>
          )}
          {showCreatePo && poActionSelection === "new" && cell != null && remainingToAllocate > 0 ? (
            <CreatePoForm
              cellId={cell.id}
              planMonth={planMonth}
              category={category}
              kitItems={kitItems}
              forecastQty={remainingToAllocate}
              expectedDeliveryDate={expectedArrivalInput}
              onClose={() => setShowCreatePo(false)}
              onGridStale={onGridStale}
              onError={onError}
              onSuccess={onSuccess}
            />
          ) : null}
        </section>

        <section className="space-y-2">
          <h3 className="text-sm font-semibold text-slate-900">Revision history</h3>
          {revisionsQuery.isLoading ? (
            <p className="text-xs text-slate-500">Loading revisions…</p>
          ) : revisionsQuery.data == null || revisionsQuery.data.revisions.length === 0 ? (
            <p className="text-xs text-slate-500">No revisions yet.</p>
          ) : (
            <ul className="space-y-1 text-xs">
              {revisionsQuery.data.revisions.map((revision) => (
                <li key={revision.id} className="rounded border border-slate-200 bg-white px-2 py-1">
                  <div className="flex flex-wrap items-baseline justify-between gap-2">
                    <span className="font-mono text-slate-700">
                      {revision.old_quantity == null ? "—" : formatNumber(revision.old_quantity)} →{" "}
                      {formatNumber(revision.new_quantity)}
                    </span>
                    <span className="text-slate-500">{revision.changed_at}</span>
                  </div>
                  <div className="flex flex-wrap items-baseline justify-between gap-2 text-slate-600">
                    <span>{revision.changed_by ?? "—"}</span>
                    {revision.note != null && revision.note.length > 0 ? (
                      <span className="italic">{revision.note}</span>
                    ) : null}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </section>
      </div>
    </div>
  );
}

export function ClampForecastWyjPoFormFields({
  locationCode,
  onLocationCodeChange,
  comments,
  onCommentsChange,
  planMonth,
  locationInputId,
  commentsInputId,
}: {
  locationCode: MrpLocation;
  onLocationCodeChange: (code: MrpLocation) => void;
  comments: string;
  onCommentsChange: (value: string) => void;
  planMonth: string;
  locationInputId: string;
  commentsInputId: string;
}): JSX.Element {
  return (
    <>
      <div className="sm:col-span-2">
        <Label>Supplier</Label>
        <p className="text-sm text-slate-800">{CLAMP_FORECAST_WYJ_SUPPLIER_LABEL}</p>
      </div>
      <div>
        <Label htmlFor={locationInputId}>Receiving location</Label>
        <select
          id={locationInputId}
          className="h-10 w-full rounded-md border border-slate-300 bg-white px-3 text-sm"
          value={locationCode}
          onChange={(event) => onLocationCodeChange(event.target.value as MrpLocation)}
        >
          {MRP_LOCATIONS.map((option) => (
            <option key={option.code} value={option.code}>
              {option.label}
            </option>
          ))}
        </select>
      </div>
      <div>
        <Label>Currency</Label>
        <p className="text-sm text-slate-800">{CLAMP_FORECAST_WYJ_DEFAULT_CURRENCY}</p>
      </div>
      <div>
        <Label>Payment terms</Label>
        <p className="text-sm text-slate-800">{CLAMP_FORECAST_WYJ_DEFAULT_PAYMENT_TERMS}</p>
      </div>
      <div className="sm:col-span-2">
        <FieldLabelWithHelp
          htmlFor={commentsInputId}
          label="Comments / special instructions"
          hoverHint={`Saved as purchase order comments. Defaults to the forecast month (${formatMonth(planMonth)}).`}
        />
        <Input
          id={commentsInputId}
          value={comments}
          onChange={(event) => onCommentsChange(event.target.value)}
          className="mt-1.5"
        />
      </div>
    </>
  );
}

export interface CreatePoFormProps {
  cellId: number;
  planMonth: string;
  category: ClampForecastCategoryRow;
  kitItems: ClampForecastKitItemRow[];
  forecastQty: number;
  expectedDeliveryDate: string;
  onClose: () => void;
  onGridStale: () => void;
  onError: (message: string) => void;
  onSuccess: (message: string) => void;
}

export function CreatePoForm({
  cellId,
  planMonth,
  category,
  kitItems,
  forecastQty,
  expectedDeliveryDate,
  onClose,
  onGridStale,
  onError,
  onSuccess,
}: CreatePoFormProps): JSX.Element {
  const [locationCode, setLocationCode] = useState<MrpLocation>("sgs");
  const [comments, setComments] = useState<string>(() =>
    buildClampForecastPurchaseOrderComments(planMonth),
  );

  const previewLines = useMemo(() => {
    if (category.is_kit) {
      return kitItems
        .filter((item) => item.quantity_per_kit > 0)
        .map((item) => ({
          sku: item.component_sku,
          quantity: Math.max(1, Math.round(forecastQty * item.quantity_per_kit)),
        }));
    }
    if (category.fg_sku == null) return [];
    return [{ sku: category.fg_sku, quantity: forecastQty }];
  }, [category, kitItems, forecastQty]);

  const createMutation = useMutation({
    mutationFn: async () => {
      return apiFetch<CreatePoResponse>(
        `/api/mrp/clamp-forecast/cells/${cellId}/create-po`,
        {
          method: "POST",
          skipRetries: true,
          body: {
            supplier_id: CLAMP_FORECAST_WYJ_SUPPLIER_DATA_ID,
            location_code: locationCode,
            currency: CLAMP_FORECAST_WYJ_DEFAULT_CURRENCY,
            payment_terms: CLAMP_FORECAST_WYJ_DEFAULT_PAYMENT_TERMS,
            comments: comments.trim() === "" ? null : comments,
            expected_delivery_date: expectedDeliveryDate.trim() === "" ? null : expectedDeliveryDate,
          },
        },
      );
    },
    onSuccess: (data) => {
      onSuccess(
        `Created ${data.po_number ?? `purchase order ${data.public_purchase_order_id}`}; linked ${data.forecast_po_links_created} lines.`,
      );
      onGridStale();
      onClose();
    },
    onError: (error) => onError(describeApiError(error)),
  });

  return (
    <div className="space-y-3 rounded border border-brand-200 bg-brand-50/50 p-3">
      <SectionTitleWithHelp
        title="Create purchase order"
        hoverHint={
          category.is_kit
            ? "One PO line per kit component (forecast quantity × per-kit multiplier)."
            : `Single PO line for ${category.fg_sku ?? "(no SKU)"} × ${formatNumber(forecastQty)}.`
        }
      />

      <div className="grid gap-3 sm:grid-cols-2">
        <ClampForecastWyjPoFormFields
          locationCode={locationCode}
          onLocationCodeChange={setLocationCode}
          comments={comments}
          onCommentsChange={setComments}
          planMonth={planMonth}
          locationInputId="clamp-forecast-location"
          commentsInputId="clamp-forecast-comments"
        />
      </div>

      <div>
        <p className="mb-1 text-xs font-medium text-slate-600">Lines to create</p>
        <ul className="space-y-0.5 rounded border border-slate-200 bg-white p-2 text-xs">
          {previewLines.length === 0 ? (
            <li className="text-slate-500">No lines computed (kit items may be missing).</li>
          ) : (
            previewLines.map((line) => (
              <li key={line.sku} className="flex justify-between font-mono text-slate-700">
                <span>{line.sku}</span>
                <span className="tabular-nums">{formatNumber(line.quantity)}</span>
              </li>
            ))
          )}
        </ul>
      </div>

      {createMutation.isError ? (
        <p role="alert" className="rounded-md border border-rose-300 bg-rose-50 px-3 py-2 text-sm text-rose-900">
          {describeApiError(createMutation.error)}
        </p>
      ) : null}

      <div className="flex justify-end gap-2">
        <Button variant="secondary" onClick={onClose} disabled={createMutation.isPending}>
          Cancel
        </Button>
        <Button
          onClick={() => createMutation.mutate()}
          disabled={previewLines.length === 0}
          isLoading={createMutation.isPending}
        >
          Create purchase order
        </Button>
      </div>
    </div>
  );
}

export function categoryCodesForMonthPoPlan(
  plan: ClampForecastMonthPoPlan,
  cellsById: Map<number, ClampForecastCellRow>,
): string[] {
  const codes = new Set<string>();
  for (const line of plan.tagged_lines) {
    const cell = cellsById.get(line.forecast_id);
    if (cell != null) codes.add(cell.category_code);
  }
  return [...codes];
}

export function ClampForecastMonthCreatePoModal({
  planMonth,
  plan,
  cellsById,
  onClose,
  onMonthGridStale,
  onError,
  onSuccess,
}: {
  planMonth: string;
  plan: ClampForecastMonthPoPlan | null;
  cellsById: Map<number, ClampForecastCellRow>;
  onClose: () => void;
  onMonthGridStale: (planMonth: string, categoryCodes: string[]) => Promise<void>;
  onError: (message: string) => void;
  onSuccess: (message: string) => void;
}): JSX.Element {
  const [createBusy, setCreateBusy] = useState(false);

  useEffect(() => {
    function onKeyDown(event: KeyboardEvent): void {
      if (event.key === "Escape" && !createBusy) onClose();
    }
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [createBusy, onClose]);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 p-4"
      role="presentation"
      onClick={() => {
        if (!createBusy) onClose();
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="clamp-forecast-month-create-po-title"
        className="max-h-[90vh] w-full max-w-2xl overflow-y-auto rounded-lg border border-slate-200 bg-white p-5 shadow-lg"
        onClick={(event) => event.stopPropagation()}
      >
        <MonthCreatePoForm
          planMonth={planMonth}
          plan={plan}
          cellsById={cellsById}
          onClose={onClose}
          onMonthGridStale={onMonthGridStale}
          onError={onError}
          onSuccess={onSuccess}
          onBusyChange={setCreateBusy}
        />
      </div>
    </div>
  );
}

export interface MonthCreatePoFormProps {
  planMonth: string;
  plan: ClampForecastMonthPoPlan | null;
  cellsById: Map<number, ClampForecastCellRow>;
  onClose: () => void;
  onMonthGridStale: (planMonth: string, categoryCodes: string[]) => Promise<void>;
  onError: (message: string) => void;
  onSuccess: (message: string) => void;
  onBusyChange?: (busy: boolean) => void;
}

export function MonthCreatePoForm({
  planMonth,
  plan,
  cellsById,
  onClose,
  onMonthGridStale,
  onError,
  onSuccess,
  onBusyChange,
}: MonthCreatePoFormProps): JSX.Element {
  const [locationCode, setLocationCode] = useState<MrpLocation>("sgs");
  const [comments, setComments] = useState<string>(() =>
    buildClampForecastPurchaseOrderComments(planMonth),
  );
  const planRef = useRef(plan);
  if (plan != null && plan.included_cell_count > 0) {
    planRef.current = plan;
  }

  useEffect(() => {
    setComments(buildClampForecastPurchaseOrderComments(planMonth));
  }, [planMonth]);

  const createMutation = useMutation({
    mutationFn: async () => {
      const activePlan = plan ?? planRef.current;
      if (activePlan == null || activePlan.included_cell_count === 0 || activePlan.mv_lines.length === 0) {
        throw new Error("No forecast cells in this month still need quantity on a purchase order.");
      }
      const data = await apiFetch<CreateMonthPoResponse>(
        `/api/mrp/clamp-forecast/months/${encodeURIComponent(planMonth)}/create-po`,
        {
          method: "POST",
          skipRetries: true,
          body: {
            supplier_id: CLAMP_FORECAST_WYJ_SUPPLIER_DATA_ID,
            location_code: locationCode,
            currency: CLAMP_FORECAST_WYJ_DEFAULT_CURRENCY,
            payment_terms: CLAMP_FORECAST_WYJ_DEFAULT_PAYMENT_TERMS,
            comments: comments.trim() === "" ? null : comments,
            expected_delivery_date: null,
          },
        },
      );
      await onMonthGridStale(planMonth, categoryCodesForMonthPoPlan(activePlan, cellsById));
      return data;
    },
    onSuccess: (data) => {
      onSuccess(
        `Created ${data.po_number ?? `purchase order ${data.public_purchase_order_id}`} for ${data.included_cell_count} forecast cell${data.included_cell_count === 1 ? "" : "s"} (${data.po_line_count} lines, ${data.forecast_po_links_created} links).`,
      );
      onClose();
    },
    onError: (error) => onError(describeApiError(error)),
  });

  const createBusy = createMutation.isPending || createMutation.isSuccess;
  useEffect(() => {
    onBusyChange?.(createBusy);
  }, [createBusy, onBusyChange]);

  const displayPlan = plan ?? planRef.current;
  const previewLines = displayPlan?.mv_lines ?? [];
  const skippedCells = displayPlan?.skipped_cells ?? [];

  if (!createBusy && (displayPlan == null || displayPlan.included_cell_count === 0)) {
    return (
      <div className="space-y-3">
        <h2
          id="clamp-forecast-month-create-po-title"
          className="text-lg font-semibold text-slate-900"
        >
          Create purchase order — {formatMonth(planMonth)}
        </h2>
        <p className="mt-1 text-sm text-slate-600">
          Every forecast cell in this month that still needs quantity on a purchase order is included in one
          hub purchase order.
        </p>
        <p className="text-sm text-slate-600">
          No forecast cells in this month still need quantity on a purchase order.
        </p>
        <div className="flex justify-end">
          <Button variant="secondary" onClick={onClose}>
            Close
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <h2
        id="clamp-forecast-month-create-po-title"
        className="text-lg font-semibold text-slate-900"
      >
        Create purchase order — {formatMonth(planMonth)}
      </h2>
      <p className="text-sm text-slate-600">
        One hub purchase order for every cell in this month that still needs quantity on purchase
        orders. Kit categories explode into one line per kit component.
      </p>

      <p className="text-sm text-slate-600">
        {displayPlan!.included_cell_count} forecast cell{displayPlan!.included_cell_count === 1 ? "" : "s"},{" "}
        {displayPlan!.mv_lines.length} PO line{displayPlan!.mv_lines.length === 1 ? "" : "s"}.
        {skippedCells.length > 0
          ? ` ${skippedCells.length} cell${skippedCells.length === 1 ? "" : "s"} skipped (already linked or not ready).`
          : null}
      </p>

      <div className="grid gap-3 sm:grid-cols-2">
        <ClampForecastWyjPoFormFields
          locationCode={locationCode}
          onLocationCodeChange={setLocationCode}
          comments={comments}
          onCommentsChange={setComments}
          planMonth={planMonth}
          locationInputId="clamp-forecast-month-location"
          commentsInputId="clamp-forecast-month-comments"
        />
      </div>

      <div>
        <p className="mb-1 text-xs font-medium text-slate-600">Lines to create</p>
        <ul className="max-h-48 space-y-0.5 overflow-auto rounded border border-slate-200 bg-white p-2 text-xs">
          {previewLines.map((line, index) => (
            <li key={`${line.sku}-${index}`} className="flex justify-between font-mono text-slate-700">
              <span>{line.sku}</span>
              <span className="tabular-nums">{formatNumber(line.quantity)}</span>
            </li>
          ))}
        </ul>
      </div>

      {createMutation.isError ? (
        <p role="alert" className="rounded-md border border-rose-300 bg-rose-50 px-3 py-2 text-sm text-rose-900">
          {describeApiError(createMutation.error)}
        </p>
      ) : null}

      <div className="flex justify-end gap-2">
        <Button variant="secondary" onClick={onClose} disabled={createMutation.isPending}>
          Cancel
        </Button>
        <Button
          onClick={() => createMutation.mutate()}
          disabled={previewLines.length === 0}
          isLoading={createMutation.isPending}
        >
          Create purchase order
        </Button>
      </div>
    </div>
  );
}
